'use client';

import React, { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import Breadcrumb from '../ui/Breadcrumb';
import { AssignmentIcon } from '../icons';
import { useLocalizedPath } from '@/src/hooks/useLocalizedPath';
import styles from '../StaticPage.module.css';

export interface RewardTermsSection {
  // Anchor id. Code-owned: the copy is admin-editable, anchors must not be.
  id: string;
  // Key under `content.` in the namespace JSON.
  contentKey: string;
  // Rendered instead of the clause list, for API-sourced content.
  slot?: React.ReactNode;
}

interface Props {
  // Top-level key of the locale JSON, e.g. 'cashbackTerms'.
  namespace: string;
  // Key under common.breadcrumb.
  breadcrumbKey: string;
  canonicalPath: string;
  sections: RewardTermsSection[];
  // `labelKey` is a key under `sidebar.` in the namespace.
  related: { labelKey: string; canonicalPath: string }[];
}

const RewardTermsLayout: React.FC<Props> = ({
  namespace,
  breadcrumbKey,
  canonicalPath,
  sections,
  related,
}) => {
  const t = useTranslations(namespace);
  const tCommon = useTranslations('common');
  const { localizeFullPath } = useLocalizedPath();

  const [activeSection, setActiveSection] = useState<string>(
    sections[0]?.id ?? '',
  );

  const titles = t.raw('sections') as string[];

  const sectionIds = useMemo(() => sections.map((s) => s.id), [sections]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      for (const id of sectionIds) {
        const element = document.getElementById(id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sectionIds]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  const breadcrumbItems = [
    { label: tCommon('breadcrumb.home'), path: '/' },
    { label: tCommon(`breadcrumb.${breadcrumbKey}`), path: canonicalPath },
  ];

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.breadcrumbBar}>
        <div className={styles.container}>
          <Breadcrumb items={breadcrumbItems} />
        </div>
      </div>

      <div className={styles.heroSection}>
        <div className={styles.heroInner}>
          <h1 className={styles.heroTitle}>{t('hero.title')}</h1>
          <p className={styles.heroSubtitle}>{t('hero.subtitle')}</p>
          <p className={styles.heroDate}>{t('hero.effectiveDate')}</p>
        </div>
      </div>

      <div className={styles.container}>
        <div className={styles.legalLayout}>
          <div>
            {sections.map(({ id, contentKey, slot }) => (
              <section id={id} key={id} className={styles.contentSection}>
                <h2 className={styles.sectionTitle}>
                  {t(`content.${contentKey}.title`)}
                </h2>
                <p className={styles.contentText}>
                  {t(`content.${contentKey}.intro`)}
                </p>
                {slot ?? (
                  <ol className={styles.numberedList}>
                    {(t.raw(`content.${contentKey}.items`) as string[]).map(
                      (item, index) => (
                        <li key={index}>{item}</li>
                      ),
                    )}
                  </ol>
                )}
              </section>
            ))}

            <div className={styles.lastUpdated}>{t('lastUpdated')}</div>
          </div>

          <div>
            <div className={styles.tocSidebar}>
              <h3 className={styles.tocTitle}>
                <AssignmentIcon fontSize="small" />
                {t('toc.title')}
              </h3>

              <nav aria-label="table of contents">
                <ul className={styles.tocList}>
                  {sections.map((section, index) => (
                    <li
                      key={section.id}
                      className={`${styles.tocItem} ${
                        activeSection === section.id ? styles.tocItemActive : ''
                      }`}
                      onClick={() => scrollToSection(section.id)}
                    >
                      {titles[index]}
                    </li>
                  ))}
                </ul>
              </nav>

              <hr className={styles.tocDivider} />

              <div className={styles.sidebarContactInfo}>
                <p>
                  <strong>{t('sidebar.companyTitle')}</strong>
                </p>
                <p>{t('sidebar.companyName')}</p>
                <p>{t('sidebar.companyRegistration')}</p>
                <p>{t('sidebar.companyAddressLine1')}</p>
                <p>{t('sidebar.companyAddressLine2')}</p>
              </div>

              <div className={styles.sidebarContactInfo}>
                <p>
                  <strong>{t('sidebar.relatedTitle')}</strong>
                </p>
                {related.map((link) => (
                  <p key={link.canonicalPath}>
                    <Link
                      href={localizeFullPath(link.canonicalPath)}
                      prefetch={false}
                    >
                      {t(`sidebar.${link.labelKey}`)}
                    </Link>
                  </p>
                ))}
              </div>

              <div className={styles.sidebarNeedHelp}>
                <h4>{t('sidebar.helpTitle')}</h4>
                <p>{t('sidebar.helpText')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RewardTermsLayout;
